// Phone number utility functions

/**
 * Normalize phone number to digits only (e.g., "7788724715")
 * Handles various formats and validates US/Canada numbers
 * @param {string} phoneNumber - Input phone number in any format
 * @returns {string|null} - Normalized phone number or null if invalid
 */
export function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return null;
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Handle different digit counts
  if (digitsOnly.length === 10) {
    // 10 digits - valid US/Canada number
    return digitsOnly;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    // 11 digits starting with 1 - remove country code
    return digitsOnly.substring(1);
  } else {
    // Invalid length for US/Canada
    return null;
  }
}

/**
 * Format phone number for display as XXX-XXX-XXXX
 * @param {string} phoneNumber - Normalized phone number (10 digits)
 * @returns {string} - Formatted phone number
 */
export function formatPhoneDisplay(phoneNumber) {
  if (!phoneNumber) return '';
  
  // If already formatted with dashes, return as-is
  const dashCount = (phoneNumber.match(/-/g) || []).length;
  if (dashCount === 2 && phoneNumber.match(/^\d{3}-\d{3}-\d{4}$/)) {
    return phoneNumber;
  }
  
  // Ensure we have 10 digits
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized || normalized.length !== 10) {
    return phoneNumber; // Return original if can't normalize
  }
  
  // Format as XXX-XXX-XXXX
  return `${normalized.slice(0, 3)}-${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

/**
 * Format phone number for E.164 format (e.g., "+17788724715")
 * @param {string} phoneNumber - Phone number in any format
 * @returns {string|null} - E.164 formatted number or null if invalid
 */
export function formatPhoneE164(phoneNumber) {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) return null;
  
  // Add +1 country code for US/Canada
  return `+1${normalized}`;
}

/**
 * Validate if phone number is valid US/Canada format
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid
 */
export function isValidPhoneNumber(phoneNumber) {
  const normalized = normalizePhoneNumber(phoneNumber);
  return normalized !== null && normalized.length === 10;
}

/**
 * Get validation error message for phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {string|null} - Error message or null if valid
 */
export function getPhoneValidationError(phoneNumber) {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return null; // Empty is valid (optional field)
  }
  
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  if (digitsOnly.length === 0) {
    return 'Please enter a valid phone number';
  } else if (digitsOnly.length < 10) {
    return 'Phone number is too short';
  } else if (digitsOnly.length === 10) {
    return null; // Valid
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return null; // Valid with country code
  } else {
    return 'Only US and Canada phone numbers are supported';
  }
}